import { Vec3, clamp, raDecToVec } from '../lib/math';
import { fetchWithin } from '../lib/net';
import { CONSTELLATION_ARABIC } from './lore';

export interface StarCatalog {
  count: number;
  /** J2000 unit vectors, xyz interleaved (epoch 2026.75 positions). */
  dir: Float32Array;
  mag: Float32Array;
  bv: Float32Array;
  hip: Uint32Array;
  /** Linear RGB tint, interleaved. */
  color: Float32Array;
  info: Map<number, StarInfo>;
  /** Star indices that carry a proper name, brightest first. */
  named: number[];
  byHip: Map<number, number>;
}

export interface StarInfo {
  i: number;
  name: string;
  bayer: string;
  flam: number;
  con: string;
  ly: number;
  spect: string;
  mag: number;
  hd: number;
  hr: number;
  variable: string;
  /** An older name the IAU never adopted, kept only for search (scripts/build-data.mjs). */
  aka: string;
  /** A companion's letter, shown where its designation would otherwise be its primary's ("α Gem B"). */
  comp?: string;
}

export interface Constellation {
  id: string;
  name: string;
  gen: string;
  en: string;
  ar: string;
  /** Transliteration of `ar`. */
  arTr?: string;
  rank: number;
  label: [number, number];
  lines: [number, number][][];
}

export interface ConstellationData {
  constellations: Constellation[];
  borders: [number, number][][];
}

export interface DeepSky {
  id: string;
  cat: string;
  m?: number;
  t: string;
  ra: number;
  dec: number;
  mag?: number;
  maj?: number;
  min?: number;
  pa?: number;
  con?: string;
  name?: string;
  /** Well known, as the named ones are, though its nickname is not used (BUILD-SPEC item 32). */
  known?: boolean;
  dir: Vec3;
}

const base = import.meta.env.BASE_URL;

/** Colour temperature from B−V (Ballesteros 2012). */
export function bvToKelvin(bv: number): number {
  return 4600 * (1 / (0.92 * bv + 1.7) + 1 / (0.92 * bv + 0.62));
}

/** Blackbody colour (sRGB 0..1, white near 6600 K), after Tanner Helland's fit. */
export function kelvinToRgb(k: number): [number, number, number] {
  const t = k / 100;
  let r: number, g: number, b: number;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  }
  if (t >= 66) b = 255;
  else if (t <= 19) b = 0;
  else b = 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  return [clamp(r, 0, 255) / 255, clamp(g, 0, 255) / 255, clamp(b, 0, 255) / 255];
}

export const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/** Decode the compact 'NST1' star format (see scripts/build-data.mjs). */
function parseNST1(bin: ArrayBuffer) {
  const dv = new DataView(bin);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== 'NST1') throw new Error('Bad star catalogue');
  const n = dv.getUint32(4, true);
  const H = 8;
  const hip = new Uint32Array(bin.slice(H, H + n * 4));
  const ra16 = new Uint16Array(bin.slice(H + n * 4, H + n * 6));
  const dec16 = new Int16Array(bin.slice(H + n * 6, H + n * 8));
  const mag8 = new Uint8Array(bin, H + n * 8, n);
  const bv8 = new Uint8Array(bin, H + n * 9, n);

  const dir = new Float32Array(n * 3);
  const mag = new Float32Array(n);
  const bv = new Float32Array(n);
  const color = new Float32Array(n * 3);
  const byHip = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    const v = raDecToVec((ra16[i] / 65536) * 360, (dec16[i] / 32767) * 90);
    dir[i * 3] = v[0];
    dir[i * 3 + 1] = v[1];
    dir[i * 3 + 2] = v[2];
    mag[i] = mag8[i] / 20 - 2;
    bv[i] = bv8[i] / 100 - 0.5;
    const rgb = kelvinToRgb(bvToKelvin(bv[i]));
    color[i * 3] = srgbToLinear(rgb[0]);
    color[i * 3 + 1] = srgbToLinear(rgb[1]);
    color[i * 3 + 2] = srgbToLinear(rgb[2]);
    if (hip[i]) byHip.set(hip[i], i);
  }
  return { n, dir, mag, bv, hip, color, byHip };
}

/**
 * Stars from magnitude 8 to 10 (Tycho-2 and Hipparcos, ESA, CC BY-NC 3.0 IGO; scripts/build-deepstars.mjs),
 * loaded only when the view is zoomed in far enough to show them.
 */
export async function loadDeepStars(): Promise<StarCatalog> {
  // 3 MB: a slow download carries on, but one that stalls fails (and is tried again later).
  const buf = await fetchWithin(`${base}data/stars-deep.bin`, (r) => {
    if (!r.ok) throw new Error(`stars-deep.bin ${r.status}`);
    return r.arrayBuffer();
  });
  const { n, dir, mag, bv, hip, color, byHip } = parseNST1(buf);
  return { count: n, dir, mag, bv, hip, color, info: new Map(), named: [], byHip };
}

export async function loadStars(): Promise<StarCatalog> {
  const [bin, infoJson] = await Promise.all([
    fetch(`${base}data/stars.bin`).then((r) => {
      if (!r.ok) throw new Error(`stars.bin ${r.status}`);
      return r.arrayBuffer();
    }),
    fetch(`${base}data/starinfo.json`).then((r) => r.json()),
  ]);
  const { n, dir, mag, bv, hip, color, byHip } = parseNST1(bin);

  const info = new Map<number, StarInfo>();
  const named: number[] = [];
  for (const r of infoJson.rows as unknown[][]) {
    const s: StarInfo = {
      i: r[0] as number,
      name: r[1] as string,
      bayer: r[2] as string,
      flam: r[3] as number,
      con: r[4] as string,
      ly: r[5] as number,
      spect: r[6] as string,
      mag: r[7] as number,
      hd: r[8] as number,
      hr: r[9] as number,
      variable: r[10] as string,
      aka: (r[11] as string) ?? '',
    };
    info.set(s.i, s);
    if (s.name) named.push(s.i);
  }
  // A companion that lost its older name ("Castor B", A7) keeps its letter where another star has the same designation:
  // "α Gem B" beside Castor's "α Gem". β² Sco, once "Acrab B", needs none.
  const desig = (s: StarInfo) => (s.bayer && s.con ? `${s.bayer} ${s.con}` : s.flam && s.con ? `${s.flam} ${s.con}` : '');
  const uses = new Map<string, number>();
  for (const s of info.values()) if (desig(s)) uses.set(desig(s), (uses.get(desig(s)) ?? 0) + 1);
  for (const s of info.values()) {
    const letter = !s.name && / ([B-Z])$/.exec(s.aka)?.[1];
    if (letter && (uses.get(desig(s)) ?? 0) > 1) s.comp = letter;
  }
  named.sort((a, b) => mag[a] - mag[b]);
  return { count: n, dir, mag, bv, hip, color, info, named, byHip };
}

export async function loadConstellations(): Promise<ConstellationData> {
  const r = await fetch(`${base}data/constellations.json`);
  const d = (await r.json()) as ConstellationData;
  // Arabic names with their vowels (lore.ts), in place of the data file's bare spellings.
  for (const c of d.constellations) {
    const a = CONSTELLATION_ARABIC[c.id];
    if (a) Object.assign(c, { ar: a.ar, arTr: a.tr });
  }
  return d;
}

export async function loadDeepSky(): Promise<DeepSky[]> {
  const r = await fetch(`${base}data/dso.json`);
  const list = (await r.json()) as Omit<DeepSky, 'dir'>[];
  return list.map((d) => ({ ...d, dir: raDecToVec(d.ra, d.dec) }));
}

/** Short display designation, e.g. "α Lyr", "α Gem B" or "HIP 12345". */
export function starDesignation(cat: StarCatalog, i: number): string {
  const s = cat.info.get(i);
  const comp = s?.comp ? ` ${s.comp}` : '';
  if (s?.bayer && s.con) return `${s.bayer} ${s.con}${comp}`;
  if (s?.flam && s.con) return `${s.flam} ${s.con}${comp}`;
  if (s?.hd) return `HD ${s.hd}`;
  if (cat.hip[i]) return `HIP ${cat.hip[i]}`;
  return 'Star';
}

export function starName(cat: StarCatalog, i: number): string {
  const s = cat.info.get(i);
  return s?.name || starDesignation(cat, i);
}
